import React, { useCallback, useState } from "react";
import {
  colors2,
  getVal,
  icons,
  setVal,
  UI_BODY_SEMIBOLD_SM,
  UI_BODY_SM,
  UI_CAPTION,
} from "oolib";
import styled from "styled-components";
import { transition } from "oolib/dist/themes/mixins";
import { CustomRangesInput } from "../WIDGET_ELEM_ConfigForm";
import { SegmentedControl } from "@/components/UI/SegmentedControl";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import {
  StyledCard as StyledSelectorCard,
  StyledInputHeader,
  StyledModifierSection,
} from "@/components/DataFieldInputSelector/styled";
import { CollapseTransition } from "../../utils/CollapseTransition";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { useVisibilityModifier } from "../../utils/useVisibilityModifier";

// --- Styled Components ---

const StyledCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${({ $expanded }) => ($expanded ? "10px" : "6px")};
  ${transition("border-radius")}
  ${({ $expanded }) => $expanded && `
    background: ${colors2.grey3};
    padding: 1rem;
    gap: 0.8rem;
  `}
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  cursor: pointer;
  color: ${colors2.grey90};
  ${transition("border-radius")}
  /* Collapsed, the header is the only thing in the card, so it carries the
     card's own surface — the visibility row sits outside it. */
  ${({ $standalone }) => $standalone && `
    background: ${colors2.grey3};
    border-radius: 6px 6px 0 0;
    padding: 1rem;
  `}
`;

/* Rows that butt against a card edge or each other: kill the selector's own
   rounding so the seams stay flat. Inside a card the last row keeps its rounding
   via flushRadius, so the selector can still round fully when its lightbox is
   open; collapsed, the card around them does the rounding. */
const StyledFlushRows = styled.div`
  ${StyledSelectorCard} {
    border-radius: ${({ $bottomRadius }) => ($bottomRadius ? `0 0 ${$bottomRadius} ${$bottomRadius}` : "0")};
  }

  ${StyledInputHeader} {
    border-radius: 0;
  }

  ${StyledSelectorCard},
  ${StyledModifierSection} {
    ${transition("border-radius", "border-color", "background")}
  }
`;

const StyledOption = styled.div`
  background: ${colors2.white};
  border: 1px solid ${({ $selected }) => ($selected ? colors2.grey90 : colors2.grey10)};
  border-radius: 6px;
  ${transition("border-color")}
`;

const StyledOptionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 1rem;
  cursor: pointer;
`;

const StyledRadio = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 0.1rem;
  border-radius: 50%;
  background: ${colors2.white};
  border: ${({ $selected }) =>
    $selected ? `4px solid ${colors2.grey90}` : `1.5px solid ${colors2.grey30}`};
`;

const StyledOptionBody = styled.div`
  border-top: 1px solid ${colors2.grey10};
`;

const StyledGroupingSection = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

// --- Constants ---

const REPRESENTATION_OPTIONS = [
  {
    value: "occurrence",
    display: "Count each value",
    desc: "See how often each value appears.",
  },
  {
    value: "histogram",
    display: "Group values into ranges",
    desc: "Group values into ranges to see their distribution.",
  },
];

const BUCKET_MODE_OPTIONS = [
  { display: "Count", value: "count" },
  { display: "Size", value: "size" },
  { display: "Custom", value: "custom" },
];

const MIN_BUCKETS = 1;
const MAX_BUCKETS = 50;
const DEFAULT_BUCKETS = 5;

const MIN_BUCKET_SIZE = 1;
const MAX_BUCKET_SIZE = 10000;
const DEFAULT_BUCKET_SIZE = 10;

// --- Main Component ---

export const NumberFieldConfig = ({
  dimensionKey,
  dimensionValue,
  dataKeys,
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
}) => {
  const isNumberInput = dimensionValue?.comp === "NumberInput";
  // Collapsed by default: the card header states the current choice, and the
  // "show all values" row stays reachable without expanding.
  const [expanded, setExpanded] = useState(false);

  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[thisWidgetConfig.id];
  // Extract dimension number from key (e.g. "dimension1" -> 1, "dimension3" -> 3)
  const dimension = parseInt(dimensionKey.replace("dimension", ""), 10) || 1;

  const representationKey = dataKeys[`${dimensionKey}NumberFieldRepresentation`];
  const bucketModeKey = dataKeys[`${dimensionKey}HistogramBucketMode`];
  const numberOfBucketsKey = dataKeys[`${dimensionKey}NumberOfBuckets`];
  const bucketSizeKey = dataKeys[`${dimensionKey}BucketSize`];
  const customRangesKey = dataKeys[`${dimensionKey}CustomRanges`];
  const hiddenKey = dataKeys[`hiddenDim${dimension}Datapoints`];
  const presetKey = dataKeys[`dim${dimension}VisibilityPreset`];
  const nKey = dataKeys[`dim${dimension}VisibilityN`];

  const existingCustomRanges = getVal(thisWidgetConfig, customRangesKey);
  const existingNumberOfBuckets = getVal(thisWidgetConfig, numberOfBucketsKey);
  const existingBucketSize = getVal(thisWidgetConfig, bucketSizeKey);
  const hasExistingBucketConfig = existingCustomRanges || existingNumberOfBuckets || existingBucketSize;

  const representation =
    getVal(thisWidgetConfig, representationKey) ||
    (hasExistingBucketConfig ? "histogram" : "occurrence");
  const bucketMode =
    getVal(thisWidgetConfig, bucketModeKey) ||
    (existingCustomRanges ? "custom" : existingBucketSize ? "size" : "count");
  const numberOfBuckets =
    getVal(thisWidgetConfig, numberOfBucketsKey) || DEFAULT_BUCKETS;
  const bucketSize =
    getVal(thisWidgetConfig, bucketSizeKey) || DEFAULT_BUCKET_SIZE;

  const updateConfig = useCallback((updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  }, [setConfig, underEditWidgetIdx]);

  // The eye on the bucket steppers — Show/Hide over the number field's
  // buckets/occurrence values. enableForNumberInput because, unlike the dim
  // sections, this eye exists precisely FOR a number field.
  const visibilityModifier = useVisibilityModifier({
    dimension,
    widgetConfig: thisWidgetConfig,
    dataKeys,
    data,
    isNumberInput,
    updateConfig,
    enableForNumberInput: true,
  });

  if (!isNumberInput) return null;

  const handleRepresentationChange = (newRepresentation) => {
    updateConfig((w) => {
      w = setVal(w, representationKey, newRepresentation);
      if (newRepresentation === "occurrence") {
        w = setVal(w, bucketModeKey, undefined);
        w = setVal(w, numberOfBucketsKey, undefined);
        w = setVal(w, bucketSizeKey, undefined);
        w = setVal(w, customRangesKey, undefined);
      } else {
        w = setVal(w, bucketModeKey, "count");
        w = setVal(w, numberOfBucketsKey, DEFAULT_BUCKETS);
      }
      return w;
    });
  };

  const handleBucketModeChange = (newMode) => {
    updateConfig((w) => {
      w = setVal(w, bucketModeKey, newMode);
      if (newMode === "count") {
        w = setVal(w, bucketSizeKey, undefined);
        w = setVal(w, customRangesKey, undefined);
        if (!getVal(w, numberOfBucketsKey)) w = setVal(w, numberOfBucketsKey, DEFAULT_BUCKETS);
      } else if (newMode === "size") {
        w = setVal(w, numberOfBucketsKey, undefined);
        w = setVal(w, customRangesKey, undefined);
        if (!getVal(w, bucketSizeKey)) w = setVal(w, bucketSizeKey, DEFAULT_BUCKET_SIZE);
      } else if (newMode === "custom") {
        w = setVal(w, numberOfBucketsKey, undefined);
        w = setVal(w, bucketSizeKey, undefined);
      }
      return w;
    });
  };

  const handleNumberOfBucketsChange = (newCount) => {
    updateConfig((w) => setVal(w, numberOfBucketsKey, newCount));
  };

  const handleBucketSizeChange = (newSize) => {
    updateConfig((w) => setVal(w, bucketSizeKey, newSize));
  };

  const selected = REPRESENTATION_OPTIONS.find((o) => o.value === representation);
  const CaretComp = expanded ? icons.CaretUp : icons.CaretDown;

  const showAllValuesRow = (flushRadius) => (
    <DataFieldInputSelector modifierOnly modifier={visibilityModifier} flushRadius={flushRadius} />
  );

  return (
    <StyledCard $expanded={expanded}>
      <StyledHeader $standalone={!expanded} onClick={() => setExpanded((prev) => !prev)}>
        <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey90 }}>
          {expanded ? "How should the number field be shown?" : selected?.display}
        </UI_BODY_SEMIBOLD_SM>
        <CaretComp size={16} weight="bold" color={colors2.grey90} />
      </StyledHeader>

      {/* Collapsed: the visibility row stays put, everything else folds away */}
      <CollapseTransition isVisible={!expanded}>
        <StyledFlushRows>{showAllValuesRow("6px")}</StyledFlushRows>
      </CollapseTransition>

      <CollapseTransition isVisible={expanded}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
          {REPRESENTATION_OPTIONS.map((opt) => {
            const isSelected = representation === opt.value;
            return (
              <StyledOption key={opt.value} $selected={isSelected}>
                <StyledOptionHeader
                  onClick={() => !isSelected && handleRepresentationChange(opt.value)}
                >
                  <StyledRadio $selected={isSelected} />
                  <div>
                    <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey90 }}>
                      {opt.display}
                    </UI_BODY_SEMIBOLD_SM>
                    <UI_CAPTION style={{ color: colors2.grey60 }}>
                      {opt.desc}
                    </UI_CAPTION>
                  </div>
                </StyledOptionHeader>

                <CollapseTransition isVisible={isSelected}>
                  <StyledOptionBody>
                    {opt.value === "occurrence" ? (
                      <StyledFlushRows $bottomRadius="6px">{showAllValuesRow("6px")}</StyledFlushRows>
                    ) : (
                      <>
                        <StyledGroupingSection>
                          <UI_BODY_SM style={{ color: colors2.grey90 }}>
                            How should values be grouped?
                          </UI_BODY_SM>

                          <SegmentedControl
                            options={BUCKET_MODE_OPTIONS}
                            value={bucketMode}
                            onChange={handleBucketModeChange}
                          />

                          <CollapseTransition isVisible={bucketMode === "custom"}>
                            <CustomRangesInput
                              dimensionKey={dimensionKey}
                              dimensionValue={dimensionValue}
                              customRangesKey={customRangesKey}
                              thisWidgetConfig={thisWidgetConfig}
                              setConfig={setConfig}
                              underEditWidgetIdx={underEditWidgetIdx}
                            />
                          </CollapseTransition>
                        </StyledGroupingSection>

                        {/* Steppers run edge to edge, flush with the card's bottom corners */}
                        <CollapseTransition isVisible={bucketMode !== "custom"}>
                          <StyledFlushRows $bottomRadius="6px">
                              <CollapseTransition isVisible={bucketMode === "count"}>
                                <DataFieldInputSelector
                                  stepper={{ label: "Number of ranges", value: numberOfBuckets, onChange: handleNumberOfBucketsChange, min: MIN_BUCKETS, max: MAX_BUCKETS }}
                                  modifier={visibilityModifier}
                                  flushRadius="6px"
                                />
                              </CollapseTransition>

                              <CollapseTransition isVisible={bucketMode === "size"}>
                                <DataFieldInputSelector
                                  stepper={{ label: "Range size", value: bucketSize, onChange: handleBucketSizeChange, min: MIN_BUCKET_SIZE, max: MAX_BUCKET_SIZE }}
                                  modifier={visibilityModifier}
                                  flushRadius="6px"
                                />
                              </CollapseTransition>
                          </StyledFlushRows>
                        </CollapseTransition>
                      </>
                    )}
                  </StyledOptionBody>
                </CollapseTransition>
              </StyledOption>
            );
          })}
        </div>
      </CollapseTransition>
    </StyledCard>
  );
};
